import { ChronologyViewType } from "@/components/MedicalChronology/Visits/types";
import type { MedChronExportCustomizationApiPayload } from "@/services/medChronExportService";
import type {
  MedChronExportCustomizationOptionsApi,
  MedChronExportGroupByApi,
  MedChronExportUiState,
  UserPreferencePutRequest,
  UserPreferenceResponse,
} from "@/services/userPreferencesService.types";

export const VISIT_HEADING_SEPARATOR_DASH = " - ";
export const VISIT_HEADING_SEPARATOR_TAB = "    ";

export type VisitHeadingSeparatorToken = "dash" | "tab";

const apiGroupByToUi = (groupBy: MedChronExportGroupByApi): ChronologyViewType =>
  groupBy ?? ChronologyViewType.DATE;

const uiGroupByToApi = (groupBy: ChronologyViewType): MedChronExportGroupByApi =>
  groupBy === ChronologyViewType.DATE ? null : groupBy;

const uiStateFromCustomizationOptions = (
  options: MedChronExportCustomizationOptionsApi,
  schemaVersion: number
): MedChronExportUiState => {
  const { data_customization, view_customization } = options;
  return {
    schemaVersion,
    collapseTherapyVisits: data_customization.collapse_therapy_visits,
    chronologyViewGroupBy: apiGroupByToUi(view_customization.chronology_view_group_by),
    chronologySortField: view_customization.chronology_sort_field,
    chronologySortDirection: view_customization.chronology_sort_direction,
    dateFormat: view_customization.date_format,
    visitHeadingTextColor: view_customization.visit_heading_text_color,
    visitHeadingUnderline: view_customization.visit_heading_underline,
    visitHeadingDateLocationSeparator: view_customization.visit_heading_date_location_separator,
  };
};

/**
 * Maps a GET/PUT response to UI state. The API's `customization_options` is always complete, so
 * this never falls back to a client-owned default. `schema_version` is carried into the UI state
 * so callers can round-trip it back on a PUT without holding on to the raw response.
 */
export const transformResponseToMedChronExportUiState = (
  response: UserPreferenceResponse
): MedChronExportUiState =>
  uiStateFromCustomizationOptions(response.value.customization_options, response.schema_version);

/**
 * Builds a PUT request from UI state. `name_format` is not yet exposed in the UI, so it is
 * written as `null` (the server's default). Harmless today, but if a name-format control is
 * ever added, thread the fetched value through `MedChronExportUiState` and set it here — otherwise
 * every save would overwrite a stored non-null `name_format`.
 */
export const transformMedChronExportUiStateToPutRequest = (
  state: MedChronExportUiState
): UserPreferencePutRequest => ({
  schema_version: state.schemaVersion,
  value: {
    customization_options: {
      data_customization: {
        collapse_therapy_visits: state.collapseTherapyVisits,
      },
      view_customization: {
        date_format: state.dateFormat,
        visit_heading_text_color: state.visitHeadingTextColor,
        visit_heading_underline: state.visitHeadingUnderline,
        visit_heading_date_location_separator: state.visitHeadingDateLocationSeparator,
        chronology_view_group_by: uiGroupByToApi(state.chronologyViewGroupBy),
        chronology_sort_field: state.chronologySortField,
        chronology_sort_direction: state.chronologySortDirection,
        name_format: null,
      },
    },
  },
});

/**
 * Maps the fetched UI state (the case physician's saved preferences) to the export API's
 * `customization_options` payload. Every field — date format, heading color, underline,
 * separator, group/sort — comes from the physician's saved values; none of it falls back to
 * the `MED_CHRON_EXPORT_LAYOUT` client constants used by the legacy store-backed export path.
 */
export const transformMedChronExportUiStateToApiPayload = (
  state: MedChronExportUiState
): MedChronExportCustomizationApiPayload => ({
  data_customization: {
    collapse_therapy_visits: state.collapseTherapyVisits,
  },
  view_customization: {
    date_format: state.dateFormat,
    visit_heading_text_color: state.visitHeadingTextColor,
    visit_heading_underline: state.visitHeadingUnderline,
    visit_heading_date_location_separator: state.visitHeadingDateLocationSeparator,
    chronology_sort_field: state.chronologySortField,
    chronology_sort_direction: state.chronologySortDirection,
    chronology_view_group_by: uiGroupByToApi(state.chronologyViewGroupBy),
  },
});

/**
 * Maps a stored separator string to a UI token. Returns `null` for any value that is neither
 * of the two supported separators, so the dropdown renders no selection rather than silently
 * mislabeling an unrecognized separator as "Tab".
 */
export const visitHeadingSeparatorToUiToken = (
  separator: string
): VisitHeadingSeparatorToken | null =>
  separator === VISIT_HEADING_SEPARATOR_DASH
    ? "dash"
    : separator === VISIT_HEADING_SEPARATOR_TAB
      ? "tab"
      : null;

export const visitHeadingSeparatorFromUiToken = (token: VisitHeadingSeparatorToken): string =>
  token === "dash" ? VISIT_HEADING_SEPARATOR_DASH : VISIT_HEADING_SEPARATOR_TAB;

/**
 * Flat field-by-field equality over the editable fields — used for form dirty-tracking, so
 * `schemaVersion` is deliberately excluded (it is server-owned, never edited in the form).
 */
export const areMedChronExportUiStatesEqual = (
  a: MedChronExportUiState,
  b: MedChronExportUiState
): boolean =>
  a.collapseTherapyVisits === b.collapseTherapyVisits &&
  a.chronologyViewGroupBy === b.chronologyViewGroupBy &&
  a.chronologySortField === b.chronologySortField &&
  a.chronologySortDirection === b.chronologySortDirection &&
  a.dateFormat === b.dateFormat &&
  a.visitHeadingTextColor === b.visitHeadingTextColor &&
  a.visitHeadingUnderline === b.visitHeadingUnderline &&
  a.visitHeadingDateLocationSeparator === b.visitHeadingDateLocationSeparator;
